import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import {
  analyticsApi,
  analyticsKeys,
  type Granularity,
  type MetricKind,
  type VenueMetricsResponse,
} from "./analyticsApi";
import { MetricCell } from "./MetricCell";
import { TrendSparkline } from "./TrendSparkline";

const RANGES = [7, 30, 90] as const;
const GRANULARITIES: Granularity[] = ["DAY", "WEEK", "MONTH"];
const METRICS: MetricKind[] = ["COVERS", "NO_SHOW_RATE", "OCCUPANCY", "WAITLIST_CONVERSION"];

/** UTC `yyyy-MM-dd` [from, to] for the "last N days" preset (to = today, inclusive). */
function rangeDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

/**
 * The `/v/:venueId/analytics` screen (tasks/cross-venue-analytics.md): the read-only group-account
 * per-venue metric comparison. MANAGER-gated by the route; the server resolves the caller's ROLE SCOPE,
 * so only venues the caller may see appear (R2) and the shape COLLAPSES to a single row when the scope
 * is one (R1). Absence (`NO_DATA`) and add-on-off (`NOT_ENABLED`) render distinctly from a real `0`
 * (edge cases 3/4/6); an in-progress period is badged partial (R5).
 */
export function AnalyticsDashboard() {
  const { venueId } = useVenue();
  const { t } = useTranslation();

  const [rangeDaysN, setRangeDaysN] = useState<number>(30);
  const [granularity, setGranularity] = useState<Granularity>("WEEK");
  const [trendMetric, setTrendMetric] = useState<MetricKind>("COVERS");
  const { from, to } = rangeDays(rangeDaysN);

  // Cursor accumulation over the resolved venue set (ADR-0022; edge case 7 large accounts) — reset when
  // the date range changes, adjusted during render (comparing against the last-seen range) rather than
  // via an effect.
  const [extra, setExtra] = useState<VenueMetricsResponse[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null | undefined>(undefined);
  const [lastRange, setLastRange] = useState({ from, to });
  if (lastRange.from !== from || lastRange.to !== to) {
    setLastRange({ from, to });
    setExtra([]);
    setNextCursor(undefined);
  }

  const comparison = useQuery({
    queryKey: analyticsKeys.comparison(venueId, from, to),
    queryFn: () => analyticsApi.comparison(from, to, undefined, 50),
  });

  const trend = useQuery({
    queryKey: [...analyticsKeys.root(venueId), "trend", trendMetric, from, to, granularity],
    queryFn: () => analyticsApi.trend(trendMetric, from, to, granularity, undefined, 50),
  });

  const items = [...(comparison.data?.page.items ?? []), ...extra];
  const effectiveCursor =
    nextCursor === undefined ? (comparison.data?.page.nextCursor ?? null) : nextCursor;
  const isComparison = comparison.data?.comparison ?? false;
  const scopeSize = comparison.data?.scopeSize ?? 0;

  async function loadMore() {
    if (!effectiveCursor) return;
    const next = await analyticsApi.comparison(from, to, effectiveCursor, 50);
    setExtra((p) => [...p, ...next.page.items]);
    setNextCursor(next.page.nextCursor);
  }

  return (
    <PageScaffold
      title={t("analytics.title")}
      actions={<RangePicker value={rangeDaysN} onChange={setRangeDaysN} />}
    >
      <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("analytics.subtitle")}</p>

      {comparison.isLoading ? (
        <p className={css({ color: "fg.muted" })}>{t("analytics.loading")}</p>
      ) : comparison.isError ? (
        <p className={css({ color: "fg.error" })} role="alert">
          {t("analytics.error")}
        </p>
      ) : items.length === 0 ? (
        <p className={css({ color: "fg.muted" })}>{t("analytics.empty")}</p>
      ) : (
        <>
          <p className={css({ color: "fg.muted", fontSize: "sm" })}>
            {t("analytics.scope", { count: scopeSize })}
          </p>
          {!isComparison && (
            <p className={css({ color: "fg.muted", fontSize: "sm", fontStyle: "italic" })}>
              {t("analytics.single")}
            </p>
          )}

          <div className={css({ overflowX: "auto" })}>
            <table className={css({ width: "full", borderCollapse: "collapse", fontSize: "sm" })}>
              <thead>
                <tr
                  className={css({
                    textAlign: "left",
                    borderBottomWidth: "1px",
                    borderColor: "border.default",
                  })}
                >
                  <Th>{t("analytics.col.venue")}</Th>
                  <Th align="right">{t("analytics.metric.covers")}</Th>
                  <Th align="right">{t("analytics.metric.noShowRate")}</Th>
                  <Th align="right">{t("analytics.metric.occupancy")}</Th>
                  <Th align="right">{t("analytics.metric.waitlistConversion")}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr
                    key={v.venueId}
                    className={css({ borderBottomWidth: "1px", borderColor: "border.default" })}
                  >
                    <td className={css({ py: "2", pr: "3", fontWeight: "medium" })}>
                      {v.venueName}
                      {v.inProgress && (
                        <span
                          className={css({
                            ml: "2",
                            fontSize: "xs",
                            color: "fg.warning",
                            borderWidth: "1px",
                            borderColor: "border.default",
                            rounded: "sm",
                            px: "1",
                          })}
                          title={t("analytics.badge.partial")}
                        >
                          {t("analytics.badge.inProgress")}
                        </span>
                      )}
                    </td>
                    <Td>
                      <MetricCell kind="COVERS" value={{ state: "VALUE", value: v.coversSeated }} />
                    </Td>
                    <Td>
                      <MetricCell kind="NO_SHOW_RATE" value={v.noShowRate} />
                    </Td>
                    <Td>
                      <MetricCell kind="OCCUPANCY" value={v.occupancy} />
                    </Td>
                    <Td>
                      <MetricCell kind="WAITLIST_CONVERSION" value={v.waitlistConversion} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {effectiveCursor && (
            <button
              type="button"
              onClick={loadMore}
              className={css({
                alignSelf: "flex-start",
                borderWidth: "1px",
                borderColor: "border.default",
                rounded: "md",
                px: "3",
                py: "1.5",
                fontSize: "sm",
                cursor: "pointer",
                _hover: { bg: "bg.subtle" },
              })}
            >
              {t("analytics.loadMore")}
            </button>
          )}

          {/* Trends render for a single-venue owner too — not just multi-venue groups. The trend query
              already runs unconditionally, and a solo restaurateur is the most common user, so gating the
              chart on `isComparison` left them staring at one table row with no way to see movement. */}
          <section className={css({ display: "flex", flexDirection: "column", gap: "3", mt: "4" })}>
            <div
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "3",
                flexWrap: "wrap",
              })}
            >
              <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
                {t("analytics.trends.title")}
              </h2>
              <div
                className={css({
                  display: "flex",
                  gap: "2",
                  alignItems: "center",
                  flexWrap: "wrap",
                })}
              >
                <MetricTabs value={trendMetric} onChange={setTrendMetric} />
                <GranularityPicker value={granularity} onChange={setGranularity} />
              </div>
            </div>
            {trend.isLoading ? (
              <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("analytics.loading")}</p>
            ) : (
              <div
                className={css({
                  display: "grid",
                  gridTemplateColumns: "minmax(8rem, 14rem) 1fr",
                  gap: "2",
                  alignItems: "center",
                })}
              >
                {(trend.data?.page.items ?? []).map((series) => (
                  <div key={series.venueId} className={css({ display: "contents" })}>
                    <span
                      className={css({
                        fontSize: "sm",
                        color: "fg.muted",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      })}
                    >
                      {series.venueName}
                    </span>
                    <TrendSparkline points={series.points} kind={trendMetric} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </PageScaffold>
  );
}

function Th({ children, align }: { children: ReactNode; align?: "right" }) {
  return (
    <th
      className={css({
        py: "2",
        pr: "3",
        fontWeight: "semibold",
        color: "fg.muted",
        textAlign: align === "right" ? "right" : "left",
      })}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: ReactNode }) {
  return <td className={css({ py: "2", pr: "3", textAlign: "right" })}>{children}</td>;
}

function RangePicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const { t } = useTranslation();
  return (
    <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}>
      <span className={css({ color: "fg.muted" })}>{t("analytics.range.label")}</span>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={css({
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "md",
          px: "2",
          py: "1",
          bg: "bg.subtle",
        })}
      >
        {RANGES.map((r) => (
          <option key={r} value={r}>
            {t(`analytics.range.${r}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function GranularityPicker({
  value,
  onChange,
}: {
  value: Granularity;
  onChange: (g: Granularity) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}>
      <span className={css({ color: "fg.muted" })}>{t("analytics.granularity.label")}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as Granularity)}
        className={css({
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "md",
          px: "2",
          py: "1",
          bg: "bg.subtle",
        })}
      >
        {GRANULARITIES.map((g) => (
          <option key={g} value={g}>
            {t(`analytics.granularity.${g}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetricTabs({ value, onChange }: { value: MetricKind; onChange: (m: MetricKind) => void }) {
  const { t } = useTranslation();
  const metricKey: Record<MetricKind, string> = {
    COVERS: "analytics.metric.covers",
    NO_SHOW_RATE: "analytics.metric.noShowRate",
    OCCUPANCY: "analytics.metric.occupancy",
    WAITLIST_CONVERSION: "analytics.metric.waitlistConversion",
  };
  return (
    <div className={css({ display: "flex", gap: "1", flexWrap: "wrap" })} role="tablist">
      {METRICS.map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          aria-selected={m === value}
          onClick={() => onChange(m)}
          className={css({
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
            px: "2",
            py: "1",
            fontSize: "xs",
            cursor: "pointer",
            bg: m === value ? "colorPalette.default" : "transparent",
            color: m === value ? "colorPalette.fg" : "fg.muted",
          })}
        >
          {t(metricKey[m])}
        </button>
      ))}
    </div>
  );
}
